import type { LocalMessage } from '@swchat/client-sdk';
import { useMemo, type DragEvent, type Ref } from 'react';
import { Bubble, type BubbleProps } from './Bubble';
import { dayFmt } from './format';

/** Tin liền nhau cùng người gửi trong vòng 5 phút → gộp (ẩn avatar/tên). */
const GROUP_WINDOW_MS = 5 * 60_000;

export interface DropHandlers {
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: () => void;
  onDrop?: (e: DragEvent) => void;
}

/** Vùng tin nhắn: chia theo ngày, mỗi tin là một `Bubble`; kéo-thả file (C1) qua `drop`. */
export function MessageList({
  messages,
  bubble,
  dragOver,
  drop,
  bottomRef,
}: {
  messages: LocalMessage[];
  /** Props của từng bong bóng (trừ `continued`, do danh sách tính). */
  bubble: (m: LocalMessage) => Omit<BubbleProps, 'continued'>;
  dragOver: boolean;
  drop: DropHandlers;
  bottomRef: Ref<HTMLDivElement>;
}) {
  const grouped = useMemo(() => {
    const out: { day: string; items: LocalMessage[] }[] = [];
    for (const m of messages) {
      const day = dayFmt.format(m.ts);
      if (out.at(-1)?.day !== day) out.push({ day, items: [] });
      out.at(-1)!.items.push(m);
    }
    return out;
  }, [messages]);

  return (
    // Vùng cuộn nhận focus (Tab) để cuộn bằng phím — yêu cầu tiếp cận (W1.6, axe `scrollable-region-focusable`).
    <div
      className={`ws-messages${dragOver ? ' drag-over' : ''}`}
      data-testid="messages"
      role="region"
      aria-label="Tin nhắn"
      tabIndex={0}
      {...drop}
    >
      {grouped.map((g) => (
        <div key={g.day} className="ws-day-group">
          <div className="ws-day">
            <span>{g.day}</span>
          </div>
          {g.items.map((m, i) => {
            const prev = g.items[i - 1];
            const continued =
              !!prev && prev.sender_user_id === m.sender_user_id && !prev.deleted && m.ts - prev.ts < GROUP_WINDOW_MS && !m.forwarded_from;
            return <Bubble key={m.id} {...bubble(m)} continued={continued} />;
          })}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
